import { startQueueSaveDebugOnce } from './queue-debug.js';
import { watchSeeksForDebug } from './seek-debug.js';
import { clearStagePresented, markStagePresented } from '../display/presentation-state.js';
import { applyStageRotation, normalizeRotationDegrees, } from '../display/stage-rotation.js';
import { isBlobUri, MEDIA_ERR_SRC_NOT_SUPPORTED, readBlobVideoUnsupported, rememberBlobVideoUnsupported, VideoLoadError, } from './video-source.js';
function objectFitFor(mode) {
    return mode === 'fill' ? 'cover' : 'contain';
}
/**
 * HTML5 image + video playback for web / Tizen browser shells.
 * Expects `localUri` to be a blob: or https: URL the media element can load.
 *
 * Signage rules:
 * - Decode / first-frame off-stage, then atomic `replaceChildren` so the TV
 *   never blanks between items (ADR 0005 — never blank the panel).
 * - `stop()` holds the last frame; pass `{ clear: true }` only on teardown.
 * - Stage rotation styles are written only when geometry actually changes.
 */
export class Html5PlaybackEngine {
    root;
    video = null;
    /** Which file the current video element holds, so a wall loop can reuse it. */
    videoKey = null;
    /**
     * How long a wall seek + restart takes on this device (seconds), learned from the last
     * one. Wall seeks aim this far ahead so playback starts on the shared timeline instead of
     * behind it (about 0.3 s on Samsung/Tizen, close to 0 on Chrome).
     */
    wallSeekLeadSeconds = 0;
    image = null;
    cancelled = false;
    scaleMode = 'fit';
    rotationDegrees = 0;
    muted = false;
    /** Set once this browser proves it can't play video from blob: URLs. */
    blobVideoUnsupported = readBlobVideoUnsupported();
    resizeObserver;
    layoutSnapshot = null;
    dwellTimer = null;
    dwellResolve = null;
    activePlayFinish = null;
    constructor(root) {
        this.root = root;
        // TEMPORARY for 86bcvk5j9, see queue-debug.ts: start at page load, so the test
        // also runs on an unpaired screen (e.g. a PR preview).
        startQueueSaveDebugOnce();
        this.applyScaleToRoot();
        this.applyRotationToRoot();
        this.resizeObserver = this.attachResizeObserver();
    }
    setScaleMode(mode) {
        this.scaleMode = mode;
        this.applyScaleToRoot();
        this.applyScaleToActiveMedia();
    }
    setRotationDegrees(degrees) {
        const next = normalizeRotationDegrees(degrees);
        this.rotationDegrees = next;
        this.applyRotationToRoot();
    }
    setMuted(muted) {
        this.muted = muted;
        if (this.video) {
            this.video.muted = muted;
            if (muted)
                this.video.setAttribute('muted', '');
            else
                this.video.removeAttribute('muted');
        }
    }
    async play(item) {
        this.cancelled = false;
        if (item.mimeType.startsWith('video/')) {
            await this.playVideo(item);
            return;
        }
        if (item.mimeType.startsWith('image/')) {
            await this.playImage(item);
            return;
        }
        throw new Error(`Unsupported mimeType: ${item.mimeType}`);
    }
    async playAt(item, positionFor, options) {
        this.cancelled = false;
        if (item.mimeType.startsWith('video/')) {
            await this.playVideo(item, positionFor, options?.onFirstFrame);
            return;
        }
        if (item.mimeType.startsWith('image/')) {
            await this.playImage(item, options?.onFirstFrame);
            return;
        }
        await this.play(item);
    }
    getPositionSeconds() {
        const video = this.video;
        if (!video || video.paused || video.seeking)
            return null;
        return video.currentTime;
    }
    async seek(seconds) {
        const video = this.video;
        if (!video || !Number.isFinite(seconds))
            return;
        await new Promise((resolve) => {
            const done = () => {
                clearTimeout(timer);
                video.removeEventListener('seeked', done);
                resolve();
            };
            const timer = setTimeout(done, 2000);
            video.addEventListener('seeked', done);
            video.currentTime = clampToDuration(video, seconds);
        });
    }
    setPlaybackRate(rate) {
        if (this.video && Number.isFinite(rate) && rate > 0)
            this.video.playbackRate = rate;
    }
    async stop(options) {
        this.cancelled = true;
        this.abortDwell();
        this.finishActivePlay();
        if (this.video) {
            this.video.pause();
        }
        if (options?.clear) {
            this.clearStage();
        }
    }
    attachResizeObserver() {
        if (typeof ResizeObserver === 'undefined')
            return null;
        const observer = new ResizeObserver(() => {
            this.applyRotationToRoot();
        });
        const target = this.root.parentElement ?? this.root;
        observer.observe(target);
        return observer;
    }
    applyScaleToRoot() {
        this.root.dataset.scale = this.scaleMode;
    }
    applyRotationToRoot() {
        this.layoutSnapshot = applyStageRotation(this.root, this.rotationDegrees, this.layoutSnapshot);
    }
    applyScaleToActiveMedia() {
        const fit = objectFitFor(this.scaleMode);
        if (this.video) {
            this.video.style.objectFit = fit;
            this.video.style.objectPosition = 'center';
        }
        if (this.image) {
            this.image.style.objectFit = fit;
            this.image.style.objectPosition = 'center';
        }
    }
    /** Shared-timeline position plus the learned seek lead, wrapped past the clip end. */
    withWallSeekLead(video, positionFor) {
        const target = positionFor(video.duration) + this.wallSeekLeadSeconds;
        const duration = video.duration;
        if (!Number.isFinite(duration) || duration <= 0)
            return target;
        const wrapped = target % duration;
        // Fallback only: holdUntilTimelineWraps() normally waits out the last moment first. If the
        // clock didn't move (stalled clock, tests), start the next loop at 0 rather than seeking into
        // the final frames, which would end at once and count a second play.
        return wrapped >= duration - Math.min(0.25, duration / 2) ? 0 : wrapped;
    }
    /**
     * A pass that would start in the last moment of the clip waits for the shared timeline to
     * wrap instead of starting the next loop early (or seeking into the final frames, which would
     * end at once and count a second play). Waits at most WALL_WRAP_HOLD_SECONDS; stop() ends it.
     */
    async holdUntilTimelineWraps(video, positionFor) {
        const duration = video.duration;
        if (!Number.isFinite(duration) || duration <= 0)
            return;
        const target = positionFor(duration) + this.wallSeekLeadSeconds;
        const remaining = duration - (target % duration);
        if (remaining > Math.min(WALL_WRAP_HOLD_SECONDS, duration / 2))
            return;
        await this.sleep(remaining * 1000);
    }
    disposeVideo(video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
    }
    /**
     * Atomically swap the stage child, then dispose any detached video so we never
     * paint an empty stage (which flashes "waiting for content").
     */
    mountMedia(el) {
        const previous = Array.from(this.root.children);
        this.root.replaceChildren(el);
        markStagePresented(this.root);
        for (const node of previous) {
            if (node instanceof HTMLVideoElement)
                this.disposeVideo(node);
        }
        this.applyScaleToRoot();
        this.applyRotationToRoot();
    }
    clearStage() {
        const previous = Array.from(this.root.children);
        this.video = null;
        this.videoKey = null;
        this.image = null;
        this.root.replaceChildren();
        clearStagePresented(this.root);
        for (const node of previous) {
            if (node instanceof HTMLVideoElement)
                this.disposeVideo(node);
        }
        this.applyScaleToRoot();
        this.applyRotationToRoot();
    }
    styleMediaBox(el) {
        el.style.width = '100%';
        el.style.height = '100%';
        el.style.objectFit = objectFitFor(this.scaleMode);
        el.style.objectPosition = 'center';
    }
    abortDwell() {
        if (this.dwellTimer !== null) {
            clearTimeout(this.dwellTimer);
            this.dwellTimer = null;
        }
        const resolve = this.dwellResolve;
        this.dwellResolve = null;
        resolve?.();
    }
    finishActivePlay() {
        const finish = this.activePlayFinish;
        this.activePlayFinish = null;
        finish?.();
    }
    sleep(ms) {
        return new Promise((resolve) => {
            this.abortDwell();
            this.dwellResolve = resolve;
            this.dwellTimer = setTimeout(() => {
                this.dwellTimer = null;
                this.dwellResolve = null;
                resolve();
            }, ms);
        });
    }
    createVideo(src) {
        const video = document.createElement('video');
        watchSeeksForDebug(video); // TEMPORARY for #114, see seek-debug.ts
        video.src = src;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.muted = this.muted;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        if (this.muted)
            video.setAttribute('muted', '');
        this.styleMediaBox(video);
        return video;
    }
    /** Blob first; straight to network once this browser proved it can't play blob video. */
    async initialVideoSource(item) {
        if (this.blobVideoUnsupported && item.resolveNetworkUrl && isBlobUri(item.localUri)) {
            return item.resolveNetworkUrl();
        }
        return item.localUri;
    }
    /** One retry from the network when a cached (blob) video fails to load. */
    async networkFallbackSource(item, triedSrc, error) {
        if (!item.resolveNetworkUrl || !isBlobUri(triedSrc) || this.cancelled)
            return null;
        const code = error instanceof VideoLoadError ? error.code : null;
        if (code === MEDIA_ERR_SRC_NOT_SUPPORTED && !this.blobVideoUnsupported) {
            this.blobVideoUnsupported = true;
            rememberBlobVideoUnsupported();
        }
        return item.resolveNetworkUrl();
    }
    async playVideo(item, positionFor, onFirstFrame) {
        const key = videoKeyFor(item);
        let seekStartedAt = Date.now();
        const current = this.video;
        let video;
        if (positionFor &&
            current &&
            this.videoKey === key &&
            current.isConnected &&
            !current.error &&
            Number.isFinite(current.duration)) {
            // Wall loop: keep the same element and seek it back onto the shared timeline.
            // A new element on every loop made Samsung/Tizen tear down and restart its single
            // hardware decoder, which showed about 0.8 s of black at each loop. After `ended`
            // the element keeps its last frame on screen while it seeks, so there's no gap.
            video = current;
            await this.holdUntilTimelineWraps(video, positionFor);
            if (this.cancelled)
                return;
            seekStartedAt = Date.now();
            await seekTo(video, this.withWallSeekLead(video, positionFor));
            if (this.cancelled)
                return;
        }
        else {
            const firstSrc = await this.initialVideoSource(item);
            video = this.createVideo(firstSrc);
            try {
                await waitForVideoReady(video);
            }
            catch (error) {
                const fallbackSrc = await this.networkFallbackSource(item, firstSrc, error);
                if (!fallbackSrc)
                    throw new Error(`Video failed: ${item.assetId}`);
                video = this.createVideo(fallbackSrc);
                try {
                    await waitForVideoReady(video);
                }
                catch {
                    throw new Error(`Video failed: ${item.assetId} (network fallback)`);
                }
            }
            if (this.cancelled)
                return;
            if (positionFor) {
                // Seek before mounting. positionFor gets this file's duration; the wall player uses
                // the server's shared duration instead, so all tiles agree.
                await this.holdUntilTimelineWraps(video, positionFor);
                if (this.cancelled)
                    return;
                seekStartedAt = Date.now();
                await seekTo(video, this.withWallSeekLead(video, positionFor));
                if (this.cancelled)
                    return;
            }
            this.image = null;
            this.video = video;
            this.videoKey = key;
            this.mountMedia(video);
        }
        await new Promise((resolve, reject) => {
            let settled = false;
            const finish = () => {
                if (settled)
                    return;
                settled = true;
                if (this.activePlayFinish === finish)
                    this.activePlayFinish = null;
                video.removeEventListener('ended', onEnded);
                video.removeEventListener('error', onError);
                resolve();
            };
            const onEnded = () => finish();
            const onError = () => {
                if (settled)
                    return;
                settled = true;
                if (this.activePlayFinish === finish)
                    this.activePlayFinish = null;
                video.removeEventListener('ended', onEnded);
                video.removeEventListener('error', onError);
                reject(new Error(`Video failed: ${item.assetId}`));
            };
            this.activePlayFinish = finish;
            if (positionFor) {
                // Seeking and starting take time, and Tizen lands on a keyframe: check once
                // playback has actually started and re-seek if we're still noticeably off.
                // (Continuous drift correction comes in a follow-up PR.)
                video.addEventListener('playing', () => {
                    // Time from the seek until playing again = how far the timeline moved meanwhile.
                    this.wallSeekLeadSeconds = Math.min(MAX_WALL_SEEK_LEAD_SECONDS, Math.max(0, (Date.now() - seekStartedAt) / 1000));
                    const want = positionFor(video.duration);
                    if (Math.abs(want - video.currentTime) > START_CORRECTION_THRESHOLD_SECONDS) {
                        video.currentTime = clampToDuration(video, this.withWallSeekLead(video, () => want));
                    }
                }, { once: true });
            }
            video.addEventListener('ended', onEnded);
            video.addEventListener('error', onError);
            // After the start seek, so the seeked frame isn't taken for playback.
            this.armFirstFrame(video, onFirstFrame);
            void video
                .play()
                .catch(async () => {
                video.muted = true;
                video.setAttribute('muted', '');
                await video.play();
            })
                .catch(onError);
            if (this.cancelled)
                finish();
        });
    }
    /**
     * Calls [onFirstFrame] once when a frame of [video] is really painted while it plays.
     * requestVideoFrameCallback where the browser has it (a frame shown while still paused, like
     * the seeked frame, doesn't count). Otherwise the `playing` event: note that it can fire
     * before a frame is painted right after a seek. Never after a cancel or for an old element.
     */
    armFirstFrame(video, onFirstFrame) {
        if (!onFirstFrame)
            return;
        let fired = false;
        const fire = () => {
            if (fired || this.cancelled || this.video !== video)
                return;
            fired = true;
            onFirstFrame();
        };
        const frames = video;
        if (typeof frames.requestVideoFrameCallback === 'function') {
            const onFrame = () => {
                if (fired || this.cancelled || this.video !== video)
                    return;
                if (video.paused) {
                    frames.requestVideoFrameCallback?.(onFrame);
                    return;
                }
                fire();
            };
            frames.requestVideoFrameCallback(onFrame);
        }
        else {
            video.addEventListener('playing', fire, { once: true });
        }
    }
    async playImage(item, onFirstFrame) {
        const img = document.createElement('img');
        img.src = item.localUri;
        img.alt = '';
        this.styleMediaBox(img);
        try {
            if (typeof img.decode === 'function') {
                await img.decode();
            }
            else {
                await waitForImageLoad(img);
            }
        }
        catch {
            throw new Error(`Image failed: ${item.assetId}`);
        }
        if (this.cancelled)
            return;
        this.video = null;
        this.image = img;
        this.mountMedia(img);
        if (onFirstFrame) {
            // Two animation frames: the image has been painted, not just mounted.
            requestAnimationFrame(() => requestAnimationFrame(() => {
                if (!this.cancelled && this.image === img)
                    onFirstFrame();
            }));
        }
        await this.sleep(Math.max(1, item.durationSeconds) * 1000);
    }
}
function waitForImageLoad(img) {
    if (img.complete && img.naturalWidth > 0)
        return Promise.resolve();
    return new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Image load failed'));
    });
}
/** A wall tile further than this from the shared timeline after starting gets one re-seek. */
const START_CORRECTION_THRESHOLD_SECONDS = 0.1;
/** Never aim more than this far ahead, whatever one slow seek measured. */
const MAX_WALL_SEEK_LEAD_SECONDS = 0.5;
function clampToDuration(video, seconds) {
    const duration = Number.isFinite(video.duration) ? video.duration : Infinity;
    return Math.min(Math.max(0, seconds), Math.max(0, duration - 0.05));
}
/** A pass that would start this close to the clip end waits for the timeline to wrap. */
const WALL_WRAP_HOLD_SECONDS = 0.25;
/** About half a frame: a seek this small only costs time (and lands on a keyframe on Tizen). */
const SEEK_SKIP_TOLERANCE_SECONDS = 0.02;
/**
 * Seek and wait for 'seeked' (with a timeout for players that never fire it). Skipped when the
 * video is already at the target, except after 'ended': play() on an ended video restarts
 * from 0, so that rewind must really happen.
 */
function seekTo(video, seconds) {
    if (!Number.isFinite(seconds) || seconds < 0)
        return Promise.resolve();
    const target = clampToDuration(video, seconds);
    if (!video.ended && Math.abs(video.currentTime - target) < SEEK_SKIP_TOLERANCE_SECONDS) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        const done = () => {
            clearTimeout(timer);
            video.removeEventListener('seeked', done);
            resolve();
        };
        const timer = setTimeout(done, 2000);
        video.addEventListener('seeked', done);
        video.currentTime = target;
    });
}
/** Wait until the first frame is ready before mounting (avoids black video flash). */
function waitForVideoReady(video) {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
        const onReady = () => {
            cleanup();
            resolve();
        };
        const onError = () => {
            cleanup();
            reject(new VideoLoadError(video.error?.code ?? null));
        };
        const cleanup = () => {
            video.removeEventListener('loadeddata', onReady);
            video.removeEventListener('error', onError);
        };
        video.addEventListener('loadeddata', onReady);
        video.addEventListener('error', onError);
        video.load();
    });
}
/** Same asset and same local file: safe to reuse the element instead of reloading it. */
function videoKeyFor(item) {
    return `${item.assetId}|${item.localUri}`;
}
//# sourceMappingURL=html5-playback-engine.js.map